/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { URL_LANGUAGES } from "@/lib/constants";

/**
 * Splits a pathname into its language prefix and the remaining path.
 *
 * @example
 *   normalizeLocalizedPath('/de/screen-recorder')
 *   // → { lang: 'de', rest: 'screen-recorder' }
 *
 *   normalizeLocalizedPath('/screen-recorder')
 *   // → { lang: null, rest: 'screen-recorder' }
 */
export function normalizeLocalizedPath(pathname: string): {
  lang: string | null;
  rest: string;
} {
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  const isLang = Boolean(
    first && (URL_LANGUAGES as readonly string[]).includes(first),
  );
  return {
    lang: isLang ? first : null,
    rest: (isLang ? parts.slice(1) : parts).join("/"),
  };
}

/**
 * Rebuilds a pathname with the given language prefix applied.
 * English ('en') produces no prefix — all other supported locales
 * get a /{lang}/ prefix prepended.
 *
 * @example
 *   switchLanguagePath('/de/screen-recorder', 'fr')
 *   // → '/fr/screen-recorder'
 *
 *   switchLanguagePath('/screen-recorder', 'de')
 *   // → '/de/screen-recorder'
 *
 *   switchLanguagePath('/de/screen-recorder', 'en')
 *   // → '/screen-recorder'
 */
export function switchLanguagePath(pathname: string, lang: string): string {
  const { rest } = normalizeLocalizedPath(pathname);
  return lang === "en" ? `/${rest}` : `/${lang}/${rest}`;
}
